import { useEffect, useState } from "react";
import { useUI } from "@/context/UIContext";
import { useAuth } from "@/context/AuthContext";
import { usePermissions } from "@/lib/permissions";
import { orgService, safetySettingsService, twoFactorService, apiKeyService, authService, notificationPreferencesService } from "@/services/api";
import { PageHeader } from "@/components/common/PageHeader";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Building2, Bell, ShieldCheck, Users, Palette, Lock, Plug, Sun, Moon, Check, Copy, RefreshCw, ShieldOff,
  Wrench, Cpu, Hexagon, Gauge, CloudLightning, Wrench as WrenchIcon, FileWarning, ParkingSquare,
} from "lucide-react";

// Keys must match `alert_types.category` in the backend exactly (see
// backend/app/models — categories are "vehicle_health", "device",
// "geofence", "driving", "weather"), and backend's DEFAULT_PREFERENCES in
// app/services/notification_preferences.py — same 5 keys, same order matters
// less than the keys matching, since preferences are looked up by name.
const ALERT_CATEGORIES = [
  { key: "vehicle_health", label: "Vehicle health alerts", icon: Wrench },
  { key: "device", label: "Device connect/disconnect alerts", icon: Cpu },
  { key: "geofence", label: "Geofence breaches", icon: Hexagon },
  { key: "driving", label: "Driving safety events", icon: Gauge },
  { key: "weather", label: "Severe weather alerts", icon: CloudLightning },
  { key: "parking", label: "Parking slot activity", icon: ParkingSquare },
];
const REMINDER_TYPES = [
  { key: "maintenance", label: "Maintenance overdue reminders", icon: WrenchIcon },
  { key: "documents", label: "Document expiry reminders", icon: FileWarning },
];

const SECTIONS = [
  { id: "organization", label: "Organization", icon: Building2 },
  { id: "notifications", label: "Notifications", icon: Bell },
  { id: "safety", label: "Safety", icon: ShieldCheck },
  { id: "users", label: "Users & Permissions", icon: Users },
  { id: "appearance", label: "Appearance", icon: Palette },
  { id: "security", label: "Security", icon: Lock },
  { id: "api", label: "API / Integrations", icon: Plug },
];

export default function Settings() {
  const [active, setActive] = useState("organization");
  const { theme, setTheme } = useUI();

  return (
    <div className="p-4 md:p-6 lg:p-8">
      <PageHeader title="Settings" subtitle="Configure your organization, alerts, safety rules and appearance." />
      <div className="grid gap-4 lg:grid-cols-[240px_1fr]">
        <div className="space-y-1">
          {SECTIONS.map((s) => (
            <button key={s.id} onClick={() => setActive(s.id)} data-testid={`settings-nav-${s.id}`}
              className={cn("flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left text-sm font-medium transition", active === s.id ? "bg-secondary text-foreground" : "text-muted-foreground hover:bg-secondary/50 hover:text-foreground")}>
              <s.icon className="h-4 w-4" /> {s.label}
            </button>
          ))}
        </div>

        <div className="space-y-4">
          {active === "organization" && <OrganizationPanel />}
          {active === "notifications" && <NotificationsPanel />}
          {active === "safety" && <SafetyPanel />}
          {active === "users" && (
            <Panel title="Users & Permissions" desc="Manage who has access and what they can do.">
              <p className="text-sm text-muted-foreground">Roles and members are managed in the Team section.</p>
              <Button variant="secondary" className="mt-4" onClick={() => (window.location.href = "/team")}>Go to Team</Button>
            </Panel>
          )}
          {active === "appearance" && (
            <Panel title="Appearance" desc="Personalize how ARC Beacon looks.">
              <div className="grid grid-cols-2 gap-3">
                {[{ id: "dark", label: "Dark", icon: Moon }, { id: "light", label: "Light", icon: Sun }].map((t) => (
                  <button key={t.id} onClick={() => setTheme(t.id)} data-testid={`theme-${t.id}`}
                    className={cn("relative flex flex-col items-center gap-3 rounded-2xl border p-6 transition", theme === t.id ? "border-primary/50 bg-primary/5" : "border-border/60 hover:border-border")}>
                    <div className={cn("flex h-16 w-full items-center justify-center rounded-xl", t.id === "dark" ? "bg-[#0a0a0b]" : "bg-white")}><t.icon className={cn("h-6 w-6", t.id === "dark" ? "text-white" : "text-zinc-800")} /></div>
                    <span className="text-sm font-medium">{t.label}</span>
                    {theme === t.id && <span className="absolute right-3 top-3 flex h-5 w-5 items-center justify-center rounded-full bg-primary text-primary-foreground"><Check className="h-3 w-3" /></span>}
                  </button>
                ))}
              </div>
            </Panel>
          )}
          {active === "security" && <SecurityPanel />}
          {active === "api" && <ApiKeyPanel />}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- Organization

function OrganizationPanel() {
  const { isAdmin } = usePermissions();
  const [org, setOrg] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { orgService.get().then(setOrg).catch(() => setOrg(null)); }, []);

  if (!org) return <Panel title="Organization" desc="Details used across reports and the interface."><p className="text-sm text-muted-foreground">Loading…</p></Panel>;

  const set = (field) => (e) => setOrg((o) => ({ ...o, [field]: e.target?.value ?? e }));

  const save = async () => {
    setSaving(true);
    try {
      await orgService.update({ name: org.name, address: org.address, timezone: org.timezone, units: org.units });
      toast.success("Settings saved");
    } catch (err) {
      toast.error(err.response?.data?.error || "Could not save organization settings");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Panel title="Organization" desc="Details used across reports and the interface.">
      {!isAdmin && (
        <p className="mb-4 text-xs text-muted-foreground">Only organization admins can edit these details.</p>
      )}
      <div className="grid gap-4 sm:grid-cols-2">
        <F label="Organization name"><Input value={org.name || ""} onChange={set("name")} disabled={!isAdmin} /></F>
        <F label="Address"><Input value={org.address || ""} onChange={set("address")} disabled={!isAdmin} /></F>
        <F label="Time zone">
          <Select value={org.timezone || "UTC"} onValueChange={set("timezone")} disabled={!isAdmin}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="UTC">UTC</SelectItem>
              <SelectItem value="America/Chicago">Central (Chicago)</SelectItem>
              <SelectItem value="America/New_York">Eastern (New York)</SelectItem>
              <SelectItem value="America/Los_Angeles">Pacific (LA)</SelectItem>
            </SelectContent>
          </Select>
        </F>
        <F label="Units">
          <Select value={org.units || "metric"} onValueChange={set("units")} disabled={!isAdmin}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="metric">Metric (km, L)</SelectItem><SelectItem value="imperial">Imperial (mi, gal)</SelectItem></SelectContent>
          </Select>
        </F>
      </div>
      {isAdmin && <SaveBar onSave={save} saving={saving} />}
    </Panel>
  );
}

// ---------------------------------------------------------------- Notifications

function NotificationsPanel() {
  const [prefs, setPrefs] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { notificationPreferencesService.get().then(setPrefs).catch(() => setPrefs(null)); }, []);

  if (!prefs) return <Panel title="Notifications" desc="Choose how and when ARC Beacon notifies you."><p className="text-sm text-muted-foreground">Loading…</p></Panel>;

  const setCategory = (key) => (checked) =>
    setPrefs((p) => ({ ...p, categories: { ...p.categories, [key]: checked } }));
  const setReminder = (key) => (checked) =>
    setPrefs((p) => ({ ...p, reminders: { ...p.reminders, [key]: checked } }));

  const save = async () => {
    setSaving(true);
    try {
      const saved = await notificationPreferencesService.update(prefs);
      setPrefs(saved);
      toast.success("Notification preferences saved");
    } catch (err) {
      toast.error(err.response?.data?.error || "Could not save notification preferences");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Panel title="Notifications" desc="Choose how and when ARC Beacon notifies you.">
      <Toggle
        label="Push notifications"
        checked={prefs.push_enabled}
        onCheckedChange={(v) => setPrefs((p) => ({ ...p, push_enabled: v }))}
      />
      <Toggle label="Email notifications" checked disabled />
      <Toggle label="SMS notifications" checked={false} disabled />
      <p className="mt-1 text-xs text-muted-foreground">
        Email notifications can't be turned off yet. SMS isn't set up for this organization.
      </p>

      <div className="mt-4 border-t border-border/60 pt-4">
        <div className="mb-3 text-xs font-medium uppercase tracking-wider text-muted-foreground">Alert preferences</div>
        {ALERT_CATEGORIES.map(({ key, label }) => (
          <Toggle
            key={key}
            label={label}
            checked={!!prefs.categories[key]}
            disabled={!prefs.push_enabled}
            onCheckedChange={setCategory(key)}
          />
        ))}
      </div>

      <div className="mt-4 border-t border-border/60 pt-4">
        <div className="mb-3 text-xs font-medium uppercase tracking-wider text-muted-foreground">Reminder preferences</div>
        {REMINDER_TYPES.map(({ key, label }) => (
          <Toggle
            key={key}
            label={label}
            checked={!!prefs.reminders[key]}
            disabled={!prefs.push_enabled}
            onCheckedChange={setReminder(key)}
          />
        ))}
      </div>

      <SaveBar onSave={save} saving={saving} />
    </Panel>
  );
}

// ---------------------------------------------------------------- Safety

function SafetyPanel() {
  const { isManager } = usePermissions();
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { safetySettingsService.get().then(setSettings).catch(() => setSettings(null)); }, []);

  if (!settings) return <Panel title="Safety" desc="Define thresholds that trigger safety alerts."><p className="text-sm text-muted-foreground">Loading…</p></Panel>;

  const topBand = settings.speed_bands?.[settings.speed_bands.length - 1];
  const speedThreshold = topBand?.min_speed ?? 110;

  const save = async () => {
    setSaving(true);
    try {
      await safetySettingsService.update({
        hard_braking_sensitivity_pct: settings.hard_braking_sensitivity_pct,
        hard_acceleration_sensitivity_pct: settings.hard_acceleration_sensitivity_pct,
        notify_geofence_exit_outside_hours: settings.notify_geofence_exit_outside_hours,
        enforce_driver_safety_rules: settings.enforce_driver_safety_rules,
        weather_alerts_enabled: settings.weather_alerts_enabled,
        speed_bands: [{ label: "Speeding", min_speed: speedThreshold, color_hex: "#f43f5e" }],
      });
      toast.success("Settings saved");
    } catch (err) {
      toast.error(err.response?.data?.error || "Could not save safety settings");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Panel title="Safety" desc="Define thresholds that trigger safety alerts.">
      {!isManager && (
        <p className="mb-4 text-xs text-muted-foreground">Only fleet managers and organization admins can edit safety settings.</p>
      )}
      <SliderRow
        label="Speed threshold"
        value={speedThreshold}
        unit="mph"
        disabled={!isManager}
        onChange={(v) => setSettings((s) => ({
          ...s,
          speed_bands: [{ label: "Speeding", min_speed: v, color_hex: "#f43f5e" }],
        }))}
      />
      <SliderRow
        label="Hard braking sensitivity"
        value={settings.hard_braking_sensitivity_pct}
        unit="%"
        max={100}
        disabled={!isManager}
        onChange={(v) => setSettings((s) => ({ ...s, hard_braking_sensitivity_pct: v }))}
      />
      <SliderRow
        label="Hard acceleration sensitivity"
        value={settings.hard_acceleration_sensitivity_pct}
        unit="%"
        max={100}
        disabled={!isManager}
        onChange={(v) => setSettings((s) => ({ ...s, hard_acceleration_sensitivity_pct: v }))}
      />
      <div className="mt-2 border-t border-border/60 pt-4">
        <Toggle
          label="Notify on geofence exit outside hours"
          checked={settings.notify_geofence_exit_outside_hours}
          onCheckedChange={(v) => setSettings((s) => ({ ...s, notify_geofence_exit_outside_hours: v }))}
          disabled={!isManager}
        />
        <Toggle
          label="Enforce driver safety rules"
          checked={settings.enforce_driver_safety_rules}
          onCheckedChange={(v) => setSettings((s) => ({ ...s, enforce_driver_safety_rules: v }))}
          disabled={!isManager}
        />
        <Toggle
          label="Severe weather alerts (National Weather Service)"
          checked={settings.weather_alerts_enabled}
          onCheckedChange={(v) => setSettings((s) => ({ ...s, weather_alerts_enabled: v }))}
          disabled={!isManager}
        />
      </div>
      {isManager && <SaveBar onSave={save} saving={saving} />}
    </Panel>
  );
}

// ---------------------------------------------------------------- Security (2FA + password)

function SecurityPanel() {
  const [twoFactor, setTwoFactor] = useState(null);
  const [enrolling, setEnrolling] = useState(null); // { secret, provisioning_uri }
  const [confirmCode, setConfirmCode] = useState("");
  const [disableOpen, setDisableOpen] = useState(false);
  const [disableCode, setDisableCode] = useState("");
  const [disablePassword, setDisablePassword] = useState("");
  const [pwOpen, setPwOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const refresh = () => twoFactorService.status().then(setTwoFactor).catch(() => setTwoFactor(null));
  useEffect(() => { refresh(); }, []);

  const startEnrol = async () => {
    setBusy(true);
    try {
      const data = await twoFactorService.enrol();
      setEnrolling(data);
    } catch (err) {
      toast.error(err.response?.data?.error || "Could not start 2FA enrolment");
    } finally {
      setBusy(false);
    }
  };

  const confirmEnrol = async () => {
    setBusy(true);
    try {
      const result = await twoFactorService.confirm(confirmCode);
      toast.success("Two-factor authentication enabled", {
        description: `Save these recovery codes: ${result.recovery_codes.join(", ")}`,
      });
      setEnrolling(null);
      setConfirmCode("");
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.error || "Invalid code");
    } finally {
      setBusy(false);
    }
  };

  const disable2fa = async () => {
    setBusy(true);
    try {
      await twoFactorService.disable(disableCode, disablePassword);
      toast.success("Two-factor authentication disabled");
      setDisableOpen(false);
      setDisableCode(""); setDisablePassword("");
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.error || "Could not disable 2FA");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Panel title="Security" desc="Protect your account and organization.">
      <div className="flex items-center justify-between py-2.5">
        <div>
          <div className="text-sm">Two-factor authentication</div>
          {twoFactor?.enabled && <div className="text-xs text-muted-foreground">{twoFactor.recovery_codes_remaining} recovery codes remaining</div>}
        </div>
        {twoFactor?.enabled ? (
          <Button size="sm" variant="secondary" onClick={() => setDisableOpen(true)} data-testid="disable-2fa"><ShieldOff className="mr-1.5 h-3.5 w-3.5" /> Disable</Button>
        ) : (
          <Button size="sm" variant="secondary" onClick={startEnrol} disabled={busy} data-testid="enable-2fa">Enable</Button>
        )}
      </div>

      {enrolling && (
        <div className="mt-3 space-y-3 rounded-xl border border-border/60 bg-secondary/20 p-4">
          <p className="text-sm text-muted-foreground">Add this to your authenticator app (Google Authenticator, Authy, …), then enter the 6-digit code it shows.</p>
          <div><div className="text-xs uppercase tracking-wider text-muted-foreground">Secret key</div><div className="font-telemetry text-sm">{enrolling.secret}</div></div>
          <div className="flex gap-2">
            <Input placeholder="123456" value={confirmCode} onChange={(e) => setConfirmCode(e.target.value)} maxLength={6} data-testid="2fa-confirm-code" />
            <Button onClick={confirmEnrol} disabled={busy || confirmCode.length < 6} data-testid="2fa-confirm-submit">Confirm</Button>
          </div>
        </div>
      )}

      <div className="mt-1">
        <Toggle label="Require SSO for all members" checked={false} disabled />
        <p className="-mt-2 mb-2 text-xs text-muted-foreground">Not available yet — enterprise SSO is planned for a future release.</p>
        <Toggle label="Auto sign-out after 30 min idle" checked disabled />
        <p className="-mt-2 text-xs text-muted-foreground">Not available yet.</p>
      </div>

      <div className="mt-4 border-t border-border/60 pt-4">
        <Button variant="secondary" onClick={() => setPwOpen(true)} data-testid="change-password">Change password</Button>
      </div>

      <Dialog open={disableOpen} onOpenChange={setDisableOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Disable two-factor authentication</DialogTitle><DialogDescription>Enter a current code and your password to confirm.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <F label="Authenticator code"><Input value={disableCode} onChange={(e) => setDisableCode(e.target.value)} maxLength={6} /></F>
            <F label="Password"><Input type="password" value={disablePassword} onChange={(e) => setDisablePassword(e.target.value)} /></F>
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setDisableOpen(false)}>Cancel</Button>
            <Button onClick={disable2fa} disabled={busy || !disableCode || !disablePassword}>Disable</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ChangePasswordDialog open={pwOpen} onOpenChange={setPwOpen} />
    </Panel>
  );
}

function ChangePasswordDialog({ open, onOpenChange }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (next !== confirm) { toast.error("New passwords don't match"); return; }
    setBusy(true);
    try {
      await authService.changePassword(current, next);
      toast.success("Password changed");
      onOpenChange(false);
      setCurrent(""); setNext(""); setConfirm("");
    } catch (err) {
      toast.error(err.response?.data?.error || "Could not change password");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader><DialogTitle>Change password</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <F label="Current password"><Input type="password" value={current} onChange={(e) => setCurrent(e.target.value)} /></F>
          <F label="New password"><Input type="password" value={next} onChange={(e) => setNext(e.target.value)} /></F>
          <F label="Confirm new password"><Input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} /></F>
        </div>
        <DialogFooter>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={busy || !current || next.length < 8}>{busy ? "Saving…" : "Change password"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------- API key

function ApiKeyPanel() {
  const { isAdmin } = usePermissions();
  const [status, setStatus] = useState(null);
  const [plaintext, setPlaintext] = useState(null); // only ever held in memory, shown once
  const [busy, setBusy] = useState(false);

  const refresh = () => apiKeyService.status().then(setStatus).catch(() => setStatus(null));
  useEffect(() => { refresh(); }, []);

  const issue = async () => {
    setBusy(true);
    try {
      const result = await apiKeyService.issue();
      setPlaintext(result.api_key);
      toast.success(result.replaced_existing ? "API key rotated" : "API key issued");
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.error || "Could not issue API key");
    } finally {
      setBusy(false);
    }
  };

  const revoke = async () => {
    setBusy(true);
    try {
      await apiKeyService.revoke();
      setPlaintext(null);
      toast.success("API key revoked");
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.error || "Could not revoke API key");
    } finally {
      setBusy(false);
    }
  };

  const copy = () => {
    navigator.clipboard?.writeText(plaintext || "");
    toast.success("API key copied");
  };

  return (
    <Panel title="API / Integrations" desc="Manage API access and connected providers.">
      {isAdmin ? (
        <>
          <F label="API Key">
            {plaintext ? (
              <div className="flex gap-2">
                <Input readOnly value={plaintext} className="font-telemetry" />
                <Button variant="secondary" onClick={copy}><Copy className="mr-1.5 h-4 w-4" /> Copy</Button>
              </div>
            ) : (
              <Input readOnly value={status?.has_key ? "A key is set — only shown once, at issue time" : "No API key issued yet"} />
            )}
          </F>
          {plaintext && <p className="mt-1.5 text-xs text-amber-700 dark:text-amber-300">Store this now — it won't be shown again.</p>}
          <div className="mt-3 flex gap-2">
            <Button variant="secondary" disabled={busy} onClick={issue} data-testid="issue-api-key"><RefreshCw className="mr-1.5 h-4 w-4" /> {status?.has_key ? "Rotate key" : "Issue key"}</Button>
            {status?.has_key && <Button variant="destructive" disabled={busy} onClick={revoke} data-testid="revoke-api-key">Revoke</Button>}
          </div>
        </>
      ) : (
        <p className="text-sm text-muted-foreground">Only organization admins can manage the API key.</p>
      )}
      <p className="mt-4 text-xs text-muted-foreground">Connected providers are managed in the Integrations section.</p>
      <Button variant="secondary" className="mt-2" onClick={() => (window.location.href = "/integrations")}>Go to Integrations</Button>
    </Panel>
  );
}

// ---------------------------------------------------------------- shared bits

function Panel({ title, desc, children }) {
  return (
    <Card className="border-border/70 bg-card/60 p-6">
      <h2 className="font-display text-lg font-medium">{title}</h2>
      <p className="mb-5 text-sm text-muted-foreground">{desc}</p>
      {children}
    </Card>
  );
}
function F({ label, children }) { return <label className="block space-y-1.5"><span className="text-sm font-medium text-foreground/90">{label}</span>{children}</label>; }
function Toggle({ label, checked, onCheckedChange, disabled }) {
  return <div className="flex items-center justify-between py-2.5"><span className={cn("text-sm", disabled && "text-muted-foreground")}>{label}</span><Switch checked={!!checked} onCheckedChange={onCheckedChange} disabled={disabled} /></div>;
}
function SliderRow({ label, value, unit, max = 150, onChange, disabled }) {
  return <div className="py-3"><div className="mb-2 flex items-center justify-between"><span className="text-sm">{label}</span><span className="font-telemetry text-sm text-muted-foreground">{value} {unit}</span></div><Slider value={[value]} onValueChange={([v]) => onChange(v)} max={max} step={5} disabled={disabled} /></div>;
}
function SaveBar({ onSave, saving }) { return <div className="mt-6 flex justify-end border-t border-border/60 pt-5"><Button onClick={onSave} disabled={saving} data-testid="settings-save">{saving ? "Saving…" : "Save changes"}</Button></div>; }
